/**
 * AudioSystem for the Pacman Classic Version
 * Mirror of other worlds' audio implementation.
 */
import { System } from '../../../engine/ecs/core/System';
import { ResourceManager } from '../../../engine/resources/ResourceManager';
import { EventManager } from '../../../engine/events/EventManager';
import { Event } from '../../../engine/events/Event';
import { EventListener } from '../../../engine/events/EventListener';
import { Logger } from '../../../engine/utils/Logger';

export class AudioSystem extends System implements EventListener {
  private resourceManager: ResourceManager;
  private eventManager: EventManager;
  private backgroundMusic: HTMLAudioElement | null = null;
  private musicPlaying: boolean = false;
  private soundVolume: number = 0.6;
  private musicVolume: number = 0.4;
  private audioEnabled: boolean = true;

  constructor(resourceManager: ResourceManager) {
    super();
    this.resourceManager = resourceManager;
    this.eventManager = EventManager.getInstance();
    this.logger = Logger.getInstance();
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.eventManager.subscribe('dot_collected', this);
    this.eventManager.subscribe('power_pellet_collected', this);
    this.eventManager.subscribe('ghost_eaten', this);
    this.eventManager.subscribe('pacman_died', this);
    this.eventManager.subscribe('level_complete', this);
    this.eventManager.subscribe('game_over', this);
  }

  public update(): void {
    if (this.audioEnabled && !this.musicPlaying) {
      this.startBackgroundMusic();
    }
  }

  public onEvent(event: Event): void {
    if (!this.audioEnabled) return;
    switch (event.getType()) {
      case 'dot_collected':
        this.play('dot_collect.wav');
        break;
      case 'power_pellet_collected':
        this.play('power_pellet.wav');
        break;
      case 'ghost_eaten':
        this.play('ghost_hit.wav');
        break;
      case 'pacman_died':
        this.play('ghost_hit.wav');
        break;
      case 'level_complete':
        this.play('level_complete.wav');
        break;
      case 'game_over':
        this.play('game_over.wav');
        break;
    }
  }

  private play(file: string): void {
    try {
      const audio = this.resourceManager.loadSound(file);
      if (!audio) return;
      audio.currentTime = 0;
      audio.volume = this.soundVolume;
      void audio.play();
    } catch {}
  }

  private startBackgroundMusic(): void {
    if (this.musicPlaying) return;
    try {
      this.backgroundMusic = this.resourceManager.loadMusic(
        'pacman_background.wav'
      );
      if (!this.backgroundMusic) return;
      this.backgroundMusic.volume = this.musicVolume;
      this.backgroundMusic.loop = true;
      this.backgroundMusic
        .play()
        .then(() => {
          this.musicPlaying = true;
        })
        .catch(() => {});
    } catch {}
  }

  // Controls
  public toggleAudio(): void {
    this.audioEnabled = !this.audioEnabled;
    if (!this.audioEnabled) {
      this.stopMusic();
    } else {
      this.startBackgroundMusic();
    }
  }

  public getSoundVolume(): number {
    return this.soundVolume;
  }
  public setSoundVolume(v: number): void {
    this.soundVolume = Math.max(0, Math.min(1, v));
  }

  public getMusicVolume(): number {
    return this.musicVolume;
  }
  public setMusicVolume(v: number): void {
    this.musicVolume = Math.max(0, Math.min(1, v));
    if (this.backgroundMusic) {
      this.backgroundMusic.volume = this.musicVolume;
    }
  }

  private stopMusic(): void {
    if (this.backgroundMusic) {
      this.backgroundMusic.pause();
      this.backgroundMusic.currentTime = 0;
    }
    this.musicPlaying = false;
  }

  public cleanup(): void {
    this.stopMusic();
    this.resourceManager.clearAudioCache();
  }
}
